import React, { useState, useEffect } from 'react';
import './app.css';
import illumass from './services/illumass';
import { useSnackbar } from 'notistack';
import * as _ from 'lodash';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  RouteProps,
} from 'react-router-dom';
import { useCookies } from 'react-cookie';
import moment from 'moment-timezone';
import SignIn from './components/sign-in';
import Devices from './components/devices';
import ApiKeys from './components/api-keys';
import Plot from './components/plot';
import Assets from './components/assets';
import { Backdrop, CircularProgress } from '@material-ui/core';
import Hardpoints from './components/hardpoints';
import { useDispatch, useSelector } from 'react-redux';
import { RarityAppState } from './store';

let keepAliveTimer: NodeJS.Timeout | undefined = undefined;

function clearKeepAliveTimer(): void {
  if (keepAliveTimer !== undefined) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = undefined;
  }
}

function setKeepAliveTimer(): void {
  if (keepAliveTimer === undefined) {
    keepAliveTimer = setInterval(() => {
      illumass.kuzzle.query({
        controller: 'rumble/entity',
        action: 'pluginReady',
      });
    }, 30000);
  }
}

function App(): JSX.Element {
  const [initialized, setInitialized] = useState(false);
  const [initializationStarted, setInitializationStarted] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [authenticated, setAuthenticated] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies();
  const [refreshJwtTimer, setRefreshJwtTimer] = useState<number | undefined>(
    undefined
  );
  const { rememberMe } = useSelector<RarityAppState, { rememberMe: boolean }>(
    (state) => {
      return {
        rememberMe: _.get(state, ['components', 'signIn', 'rememberMe'], false),
      };
    }
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (authenticated && refreshJwtTimer === undefined) {
      illumass.auth.checkToken(illumass.auth.getJwt()).then((status) => {
        if (!status.valid) {
          return;
        }

        const tokenTtl = moment.duration(
          process.env.REACT_APP_TOKEN_TTL ?? 'PT1H'
        );
        const expiresAt = moment(status.expiresAt).utc();
        const refreshAt = expiresAt
          .clone()
          .subtract(tokenTtl.asMilliseconds() / 2);

        // Note that setTimeout has a maximum of ~24 days (2147483647 ms)
        const timeout = Math.min(
          Math.max(0, refreshAt.valueOf() - moment.now()),
          2147483647
        );
        const timer = window.setTimeout(() => {
          illumass.auth
            .refreshToken({ expiresIn: tokenTtl.toISOString() })
            .then((refreshStatus) => {
              if (process.env.NODE_ENV === 'development') {
                enqueueSnackbar(`Token refreshed at ${moment().toString()}.`);
              }
              if (rememberMe) {
                setCookie('rarity.jwt', refreshStatus.jwt, {
                  httpOnly: false,
                  sameSite: 'lax',
                });
              }
            })
            .then(() => setRefreshJwtTimer(undefined));
        }, timeout);
        setRefreshJwtTimer(timer);
      });
    } else if (!authenticated && refreshJwtTimer !== undefined) {
      window.clearTimeout(refreshJwtTimer);
      setRefreshJwtTimer(undefined);
    }
  }, [
    authenticated,
    refreshJwtTimer,
    cookies,
    enqueueSnackbar,
    setCookie,
    rememberMe,
  ]);

  useEffect(() => {
    if (!initialized && !initializationStarted) {
      setInitializationStarted(true);
      illumass.kuzzle.on('connected', () => {
        setKeepAliveTimer();
        if (process.env.NODE_ENV === 'development') {
          enqueueSnackbar('Connected.', {
            variant: 'info',
            preventDuplicate: true,
          });
        }

        const jwt = cookies['rarity.jwt'];
        if (jwt) {
          illumass.auth
            .setJwt(jwt)
            .then((b) => {
              setAuthenticated(b);
            })
            .catch((err) => {
              enqueueSnackbar(_.get(err, ['message'], 'Failed to set JWT'), {
                variant: 'error',
              });
              setAuthenticated(false);
            });
        }
      });

      illumass.kuzzle.on('reconnected', () => {
        setKeepAliveTimer();
        if (process.env.NODE_ENV === 'development') {
          enqueueSnackbar('Reconnected.', {
            variant: 'info',
            preventDuplicate: true,
          });
        }

        const jwt = cookies['rarity.jwt'];
        if (jwt) {
          illumass.auth
            .setJwt(jwt)
            .then((b) => {
              setAuthenticated(b);
            })
            .catch((err) => {
              enqueueSnackbar(_.get(err, ['message'], 'Failed to set JWT'), {
                variant: 'error',
              });
              setAuthenticated(false);
            });
        }
      });
      illumass.kuzzle.on('disconnected', () => {
        clearKeepAliveTimer();
        if (process.env.NODE_ENV === 'development') {
          enqueueSnackbar('Disconnected.', {
            variant: 'warning',
            preventDuplicate: true,
          });
        }
      });
      illumass.kuzzle.on('networkError', (err: Error | string) => {
        const msg = _.get(err, ['message'], `${err}`);
        enqueueSnackbar(msg, { variant: 'error', preventDuplicate: true });
      });
      illumass.kuzzle.on('loginAttempt', (data: object) => {
        const success: boolean = _.get(data, ['success'], false);
        if (success && rememberMe) {
          setCookie('rarity.jwt', illumass.auth.getJwt(), {
            httpOnly: false,
            sameSite: 'lax',
          });
        } else {
          removeCookie('rarity.jwt', { httpOnly: false, sameSite: 'lax' });
        }
        if (success !== authenticated) {
          setAuthenticated(success);
        }
      });
      illumass.kuzzle.on('tokenExpired', () => {
        enqueueSnackbar('Session expired. Please login again.');
        removeCookie('rarity.jwt', { httpOnly: false, sameSite: 'lax' });
        setAuthenticated(false);
      });

      illumass.connect().then(() => {
        const jwt = _.get(cookies, ['rarity.jwt']);
        if (jwt !== undefined) {
          illumass.auth
            .checkToken(jwt)
            .then((jwtStatus) => {
              if (jwtStatus.valid) {
                illumass.kuzzle.jwt = jwt;
              } else {
                dispatch({ type: 'logout' });
              }
              setAuthenticated(jwtStatus.valid);
              setInitialized(true);
            })
            .catch((err) => {
              enqueueSnackbar(
                _.get(err, ['message'], 'Unexpected initialization failure.'),
                { variant: 'error' }
              );
              setInitialized(true);
            });
        } else {
          setInitialized(true);
        }
      });
    }
  }, [
    initialized,
    initializationStarted,
    authenticated,
    cookies,
    enqueueSnackbar,
    setCookie,
    removeCookie,
    rememberMe,
    dispatch,
  ]);

  useEffect(() => {
    if (authenticated) {
      illumass.auth
        .getCurrentUser()
        .then((user) => dispatch({ type: 'login', user }));
    }
  }, [authenticated, dispatch]);

  if (initialized) {
    return (
      <div className="App">
        <Router>
          <Switch>
            <PrivateRoute authenticated={authenticated} path="/plot">
              <Plot title="Plot" />
            </PrivateRoute>
            <PrivateRoute authenticated={authenticated} path="/assets">
              <Assets />
            </PrivateRoute>
            <PrivateRoute authenticated={authenticated} path="/hardpoints">
              <Hardpoints />
            </PrivateRoute>
            <PrivateRoute authenticated={authenticated} path="/devices">
              <Devices />
            </PrivateRoute>
            <PrivateRoute authenticated={authenticated} path="/api-keys">
              <ApiKeys />
            </PrivateRoute>
            <Route path="/signin">
              <SignIn />
            </Route>
            <Route path="/">
              <Redirect
                to={{
                  pathname: '/devices',
                  state: { from: { pathname: '/' } },
                }}
              />
            </Route>
          </Switch>
        </Router>
      </div>
    );
  } else {
    return (
      <Backdrop open={true}>
        <CircularProgress />
      </Backdrop>
    );
  }
}

interface PrivateRouteProps extends RouteProps {
  authenticated: boolean;
}

function PrivateRoute(props: PrivateRouteProps): JSX.Element {
  const { children, authenticated, ...rest } = props;
  return (
    <Route
      {...rest}
      render={({ location }): React.ReactNode =>
        authenticated ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: '/signin',
              state: { from: location },
            }}
          />
        )
      }
    />
  );
}

export default App;
